import React, { useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Drawer,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    IconButton,
    Typography,
    Button,
    useMediaQuery,
    Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import TokenIcon from "@mui/icons-material/VpnKey";
import PlanIcon from "@mui/icons-material/Assignment";
import LogoutIcon from "@mui/icons-material/Logout";
import ChatIcon from "@mui/icons-material/Chat";
import { getSession } from "../../utils/utils";
import { drawerStyles, buttonStyles } from "./Sidebar.styles";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';

const PAGES = {
    home: [
        { name: "Profile", icon: <PeopleIcon />, path: "/user" },
        { name: "Tokens", icon: <TokenIcon />, path: "/tokens" },
        { name: "Shika AI", icon: <ChatIcon />, path: "/chat" },
        { name: "Billing", icon: <CreditCardIcon />, path: "/billing" },
    ],
    bottom: [{ name: "Accounts", icon: <PlanIcon />, path: "/accounts" }],
};

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery("(max-width: 600px)");
    const isNarrowScreen = useMediaQuery("(max-width: 1800px)");

    const session = useMemo(() => getSession() || {}, []);
    const drawerWidth = isMobile ? "80%" : isNarrowScreen ? "18%" : "240px";

    const renderPageList = (pages) =>
        pages.map((page) => {
            const isActive = location.pathname.startsWith(page.path);

            return (
                <ListItem key={page.name} disablePadding>
                    <ListItemButton
                        component={Link}
                        to={page.path}
                        sx={buttonStyles.listItemButton(isActive)}
                    >
                        <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: 2 }}>
                            {page.icon}
                        </ListItemIcon>
                        <ListItemText primary={page.name} />
                    </ListItemButton>
                </ListItem>
            );
        });

    return (
        <>
            {isMobile && (
                <IconButton
                    onClick={handleDrawerToggle}
                    sx={{ position: "absolute", top: 16, left: 8, color: "#464646" }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={drawerStyles.drawer(drawerWidth)}
            >
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    {/* Заголовок */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 64,
                            px: 2,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 400,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: "pointer",
                            }}
                            onClick={() => navigate("/")}
                        >
                            Horizon Data Wave
                        </Typography>
                    </Box>

                    <Divider sx={{ bgcolor: "#444" }} />

                    {/* Верхний блок: навигация */}
                    <List>{renderPageList(PAGES.home)}</List>
                    <List>{renderPageList(PAGES.bottom)}</List>

                    {/* Заполнитель для выталкивания Support к низу верхнего блока */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* Кнопка Support с выделением цветом */}
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton
                                component="a"
                                href="mailto:support@horizondatawave.ai"
                                sx={{
                                    ...buttonStyles.listItemButton(false),
                                    backgroundColor: "#ffcc00",
                                    "&:hover": {
                                        backgroundColor: "#ffdd33",
                                    },
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: 2 }}>
                                    <EmailIcon />
                                </ListItemIcon>
                                <ListItemText primary="Support" />
                            </ListItemButton>
                        </ListItem>
                    </List>

                    <Divider sx={{ bgcolor: "#444" }} />

                    {/* Нижний блок: информация о пользователе и кнопка Logout */}
                    <Box sx={{ textAlign: "center", py: 2 }}>
                        <Tooltip title="User settings">
                            <IconButton sx={{ width: 48, height: 48 }}>
                                <Avatar alt="User Avatar" />
                            </IconButton>
                        </Tooltip>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                            {session.login || "Unknown"}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<LogoutIcon />}
                            onClick={() => navigate("/logout")}
                            sx={{ mt: 2, color: "#ccc", borderColor: "#666" }}
                        >
                            Logout
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default Sidebar;